import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GENERATED_SPECIES_DATA } from '@/generated/species-catalog';
import { spectacleByCode } from '@/lib/bloom';

export interface Species {
  id: string;
  nameKey: string;
  scientificName: string;
  category: 'mushroom' | 'plant' | 'berry' | 'nut' | 'flower';
  emoji: string;
  descriptionKey: string;
  howToKey: string;
  season?: string;
  habitat?: string;
  showOnMap?: boolean;
  forecastRegions?: readonly ForecastRegion[];
  /** A map layer scored as the best of these species, not a taxon of its own. */
  compositeOf?: readonly string[];
}

export type ForecastRegion = 'NE' | 'SE' | 'USE' | 'USW';

export interface SpeciesOption {
  code: string;
  emoji: string;
  category: Species['category'];
}

export interface SpeciesWithTranslations extends Omit<
  Species,
  'nameKey' | 'descriptionKey' | 'howToKey'
> {
  name: string;
  description: string;
  howTo: string;
}

export const SPECIES_DATA: Species[] = GENERATED_SPECIES_DATA;

export const useSpeciesData = (): SpeciesWithTranslations[] => {
  const { t } = useTranslation('species', { keyPrefix: 'list_of_species' });

  return useMemo(
    () =>
      SPECIES_DATA.map(species => ({
        ...species,
        name: t(species.nameKey),
        description: t(species.descriptionKey),
        howTo: t(species.howToKey),
      })),
    [t]
  );
};

export const getSpeciesById = (id: string): Species | undefined =>
  SPECIES_DATA.find(species => species.id === id);

/**
 * The `list_of_species.*` name key for an id. Keys follow the manifest's
 * translationKey, which differs from the id for a few species
 * (`chicken-of-the-woods` → `chickenOfTheWoods`), so `${id}.name` is not safe.
 */
export const speciesNameKey = (id: string): string =>
  getSpeciesById(id)?.nameKey ?? `${id}.name`;

export const getSpeciesByCategory = (
  category: Species['category']
): Species[] => SPECIES_DATA.filter(species => species.category === category);

export const getAllSpecies = (): Species[] => SPECIES_DATA;

/** The species-type filter chips (map species panel, "Species in this area",
 * Species page): `labelKey` is in the `species` namespace, the icon is
 * `CATEGORY_ICONS[value]`. */
export const CATEGORY_FILTERS: readonly {
  value: Species['category'];
  labelKey: string;
}[] = [
  { value: 'mushroom', labelKey: 'mushrooms' },
  { value: 'plant', labelKey: 'plants' },
  { value: 'berry', labelKey: 'berries' },
  { value: 'nut', labelKey: 'nuts' },
  { value: 'flower', labelKey: 'flowers' },
];

// region is mandatory on purpose: an optional one degraded to "no filter", so a
// caller that forgot it silently got the all-regions list back (parasol selectable
// in the US, asparagus in NE). Now that mistake is a type error.
export const getSpeciesOptions = (region: ForecastRegion): SpeciesOption[] =>
  SPECIES_DATA.filter(
    ({ showOnMap, forecastRegions }) =>
      showOnMap && forecastRegions?.includes(region)
  ).map(({ id, emoji, category }) => ({ code: id, emoji, category }));

/** Boletus (porcini & bay bolete), the best of those boletes: forecast in every region,
 * so it is always a valid map fallback. */
export const DEFAULT_MAP_SPECIES = 'boletus';

/**
 * A species the map can show somewhere — the check a URL or saved code must pass.
 * The spectacle layers (cherry blossom, ...) pass too: they are picked like a
 * species, but they are not in the catalog (see lib/bloom.ts).
 */
export const isMapSpecies = (code: string | null | undefined): boolean =>
  !!code &&
  (spectacleByCode(code) !== undefined ||
    SPECIES_DATA.some(({ id, showOnMap }) => id === code && showOnMap));
